import { isNil } from 'lodash';

import { ClaimConductorStatusResponseConductorStatus as ConductorStatus } from '~/server_shared/openapi/data-contracts';

/** One reading of Clive's status on the claim: an answer of the status route, or a document of its topic. */
export interface ConductorStatusObservation {
  /** As claims_llm sent it, so possibly a status this release does not know; null before Clive's first run. */
  conductorStatus: string | null;
  isActive: boolean;
  /** The asks waiting on the handler. Only the status route counts them; a published status has none. */
  openAsks?: number;
  /** The run the status is about: null before Clive's first run, absent from a document that names none. */
  runId?: string | null;
}

/** The status a document of the `conductor_status` topic carries; null when it has no status string and `is_active` boolean. */
export const toPublishedObservation = (
  metadata: Record<string, unknown> | undefined
): ConductorStatusObservation | null => {
  const conductorStatus = metadata?.conductor_status;
  const isActive = metadata?.is_active;
  if (typeof conductorStatus !== 'string' || typeof isActive !== 'boolean') return null;
  const runId = metadata?.run_id;
  return { conductorStatus, isActive, runId: typeof runId === 'string' ? runId : undefined };
};

const KNOWN_STATUSES: ReadonlySet<string> = new Set(Object.values(ConductorStatus));

/** Whether this release knows the status; claims_llm can ship one before CMS does. */
export const isKnownStatus = (status: string): status is ConductorStatus => KNOWN_STATUSES.has(status);

/**
 * Whether a run is executing now, which `isActive` does not say: it holds as well once a run has ended waiting on
 * experts, whose answers wake the next. A run still preparing counts: its row is committed, though no worker has it
 * yet. A status this release does not know, or none, reads by `isActive`.
 */
const isExecuting = ({ conductorStatus, isActive }: ConductorStatusObservation): boolean => {
  if (isNil(conductorStatus) || !isKnownStatus(conductorStatus)) return isActive;
  return conductorStatus === ConductorStatus.Preparing || conductorStatus === ConductorStatus.Running;
};

/**
 * Whether the status says a run is executing on the claim now; nothing is known to be working before it is read.
 * Waiting on experts is not a run working: the run has ended with consults open, and their answers wake the next.
 */
export const isRunWorking = (observation: ConductorStatusObservation | null): boolean =>
  !isNil(observation) && isExecuting(observation);

/**
 * Whether the status says the run is over: no run is executing and the status is about that run or names none
 * (so a run that ended waiting on experts is over, though the status reads active), or another run is executing,
 * since Clive runs one run at a time on a claim. A finished run the status names is not taken for this one, which
 * may have started since and not yet published; that this one is over then shows in the stream, as entries of a
 * later run.
 */
export const hasRunEnded = (observation: ConductorStatusObservation | null, runId: string | null): boolean => {
  if (!observation) return false;
  if (isExecuting(observation)) return !isNil(observation.runId) && !isNil(runId) && observation.runId !== runId;
  return isNil(observation.runId) || isNil(runId) || observation.runId === runId;
};
